import { useEffect, useRef } from 'react';
import type { Branding, ClosingScreen as ClosingScreenData } from '@branchworks/shared';
import { readableForeground } from './branding';

/**
 * The optional end screen, shown after the learner reaches an outcome and
 * advances past it (when the scenario carries a `closing`). The session has
 * already completed (session_completed fired when the outcome was entered);
 * this screen is the conclusion wrapper.
 *
 * The primary button is EXIT. With no LMS present it degrades gracefully via
 * `onExit` (the App attempts window.close() and otherwise shows an exited
 * confirmation). A later slice maps EXIT onto the SCORM finish call.
 *
 * Accessibility mirrors the title screen: <main>/<h1>, focus-to-heading on
 * mount, AA-legible foreground chosen for the brand background.
 */
export function EndScreen({
  closing,
  branding,
  onExit,
  onRestart,
}: {
  closing: ClosingScreenData;
  branding?: Branding;
  onExit: () => void;
  onRestart: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const { bg, fg, faint } = readableForeground(branding?.backgroundColor);

  return (
    <main
      aria-label="End screen"
      className="min-h-screen flex items-center justify-center p-8"
      style={{ background: bg, color: fg }}
    >
      <div className="max-w-2xl w-full text-center">
        {branding?.logoUrl && (
          <img
            src={branding.logoUrl}
            alt=""
            className="mx-auto mb-8 max-h-28 w-auto"
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
          />
        )}
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-4xl font-bold leading-tight mb-6 focus:outline-none"
        >
          {closing.heading}
        </h1>
        {closing.body && (
          <p className="text-lg leading-relaxed mb-10 mx-auto max-w-xl" style={{ color: faint }}>
            {closing.body}
          </p>
        )}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={onExit}
            className="px-8 py-3 rounded text-base font-semibold tracking-wide"
            style={{ background: fg, color: bg }}
          >
            {closing.buttonLabel || 'Exit'}
          </button>
          <button
            onClick={onRestart}
            className="px-5 py-3 rounded text-base font-medium border"
            style={{ borderColor: fg, color: fg }}
          >
            <span aria-hidden="true">↻</span> Start over
          </button>
        </div>
      </div>
    </main>
  );
}
